
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  FileText, 
  Search, 
  Filter, 
  Clock, 
  Download,
  AlertCircle,
  User,
  Bot
} from 'lucide-react';

const HQLogs = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const logs = [
    {
      id: 1,
      timestamp: "2024-02-10T15:30:45",
      action: "User login",
      user: "sarah.johnson@juv.com",
      userType: "staff",
      details: "Staff login from Dallas location",
      level: "info"
    },
    {
      id: 2,
      timestamp: "2024-02-10T14:22:18",
      action: "AI agent action",
      user: "Alma",
      userType: "ai",
      details: "Training module updated: Botox Injection Techniques",
      level: "info"
    },
    {
      id: 3,
      timestamp: "2024-02-10T13:05:36",
      action: "System error",
      user: "system",
      userType: "system", 
      details: "Database connection timeout (recovered automatically)",
      level: "error"
    },
    {
      id: 4,
      timestamp: "2024-02-10T12:48:12",
      action: "Patient update",
      user: "michael.chen@juv.com",
      userType: "staff",
      details: "Updated patient record #12845 (treatment plan)",
      level: "info"
    },
    {
      id: 5,
      timestamp: "2024-02-10T11:32:05",
      action: "Security alert",
      user: "system",
      userType: "system",
      details: "Multiple failed login attempts from IP 203.45.109.22",
      level: "warning"
    }
  ];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'info': return 'bg-blue-500/20 text-blue-200 border-blue-500/30';
      case 'warning': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'error': return 'bg-red-500/20 text-red-200 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getUserTypeIcon = (userType: string) => {
    switch (userType) {
      case 'staff': return <User className="w-4 h-4" />;
      case 'ai': return <Bot className="w-4 h-4" />;
      case 'system': return <AlertCircle className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                System Logs
              </h1>
              <p className="mt-1 text-amber-200">
                Audit trails and system activity monitoring
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              Export Logs
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Search and Filters */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    Level
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    User Type
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Clock className="w-4 h-4 mr-2" />
                    Date Range
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Log Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <FileText className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Total Logs</p>
                    <p className="text-2xl font-bold text-white">5,281</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-red-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-red-500/20">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-red-200">Errors (Today)</p>
                    <p className="text-2xl font-bold text-white">3</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <Bot className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">AI Actions</p>
                    <p className="text-2xl font-bold text-white">187</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Logs */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Recent System Activity</CardTitle>
            </CardHeader>
            <CardContent className="overflow-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-600/30">
                    <th className="text-left text-slate-300 font-medium py-3 px-4">Time</th>
                    <th className="text-left text-slate-300 font-medium py-3 px-4">Action</th>
                    <th className="text-left text-slate-300 font-medium py-3 px-4">User</th>
                    <th className="text-left text-slate-300 font-medium py-3 px-4">Details</th>
                    <th className="text-left text-slate-300 font-medium py-3 px-4">Level</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-600/10 hover:bg-slate-700/20">
                      <td className="py-3 px-4 text-sm text-slate-300">
                        {formatDateTime(log.timestamp)}
                      </td>
                      <td className="py-3 px-4 text-sm text-white">
                        {log.action}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300">
                        <div className="flex items-center space-x-2">
                          {getUserTypeIcon(log.userType)}
                          <span>{log.user}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300">
                        {log.details}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <Badge className={getLevelColor(log.level)}>
                          {log.level}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Showing 5 of 5,281 logs</p>
            <Button variant="outline" className="border-slate-600 text-slate-300">
              Load More
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HQLogs;
